import { Component, OnInit } from '@angular/core';
import { HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { ServicoService } from '../../../services/servico.service';
import {
  ApexAxisChartSeries,
  ApexChart,
  ApexXAxis,
  ApexNonAxisChartSeries,
} from 'ng-apexcharts';


interface VistoriaItem {
  // fields based on `obterTodos` response sample
  cadastroId?: string;
  recadastroId?: string;
  usuarioId?: string;
  tipoVistoria?: string; // may not exist, keep for compatibility
  status?: string; // status field in sample
  dataCadastroOriginal?: string;
  dataRecadastramento?: string;
  endereco?: {
    cidade?: string | null;
    logradouro?: string | null;
    bairro?: string | null;
    cep?: string | null;
    estado?: string | null;
    numero?: string | null;
  } | null;
  nomeConsumidor?: string;
  matricula?: string | null;
  numeroOS?: string | null;
}

@Component({
  selector: 'app-dashboard-admin',
  standalone: false,
  templateUrl: './dashboard-admin.component.html',
  styleUrls: ['./dashboard-admin.component.css']
})
export class DashboardAdminComponent implements OnInit {
  periodoSelecionado = 'hoje';
  dataInicialInput = '';
  dataFinalInput = '';
  municipioSelecionado = '';
  municipiosOptions: string[] = [];

  // Aggregates
  distinctMunicipios = 0;

  // Chart data
  // ApexCharts configs
  public cadastrosSeries: ApexAxisChartSeries = [{ name: 'Cadastros', data: [] }];
  public cadastrosChart: ApexChart = { type: 'bar', height: 240 };
  public cadastrosXAxis: ApexXAxis = { categories: [] };
  // combined stacked chart (cadastros + recadastros)
  // Show totals per day (cadastros + recadastros) according to date filter
  public combinedSeries: ApexAxisChartSeries = [ { name: 'Total por dia', data: [] } ];
  // combined chart shows totals (bar) and delta (line)
  public combinedChart: ApexChart = { type: 'bar', height: 320, stacked: false } as any;
  // secondary config for stroke and tooltip
  public combinedStroke: any = { width: [12, 3], curve: 'smooth' };
  public combinedTooltip: any = {
    shared: true,
    intersect: false,
    y: {
      formatter: function (val: number, opts: any) {
        // If series is delta (index 1), show sign
        try {
          if (opts && opts.seriesIndex === 1) {
            return (val > 0 ? '+' + val : val) + ' (var)';
          }
        } catch (e) {}
        return val;
      }
    }
  };
  public combinedYAxis: any = [
    { // primary y-axis for totals
      title: { text: 'Total' }
    },
    { // secondary y-axis for deltas
      opposite: true,
      title: { text: 'Variação' }
    }
  ];
  public combinedXAxis: ApexXAxis = { categories: [] };
  // daily breakdown exposed for template (labels, totals, deltas)
  public dailyLabels: string[] = [];
  public dailyTotals: number[] = [];
  public dailyDeltas: number[] = []; // absolute change from previous day
  public dailyDeltaPercents: number[] = []; // percent change from previous day
  public dailyCad: number[] = [];
  public dailyRecad: number[] = [];
  public combinedColors: string[] = ['#0ea5e9', '#7c3aed']; // blue for cad, purple for recad
  public combinedPlotOptions: any = {
    bar: {
      horizontal: false,
      columnWidth: '45%',
      endingShape: 'rounded'
    }
  };
  public combinedDataLabels: any = {
    enabled: true,
    style: { colors: ['#ffffff'] },
    formatter: function (val: number) { return val ? String(val) : ''; }
  };

  public statusSeries: ApexNonAxisChartSeries = [0, 0, 0, 0];
  public statusLabels: string[] = ['Concluídos', 'Pendentes', 'Negados', 'Outros'];
  public statusChart: ApexChart = { type: 'donut', height: 220 };

  // Municipality table data
  public municipioRows: Array<{
    nome: string;
    cadastros: number;
    recadastros: number;
    cadPercent: number;
    recadPercent: number;
  }> = [];

  // combined pie for vistorias (cadastros + recadastros)
  public vistoriasPieSeries: ApexNonAxisChartSeries = [];
  public vistoriasPieLabels: string[] = [];
  public vistoriasPieColors: string[] = ['#7c3aed', '#6d28d9', '#a78bfa', '#c4b5fd', '#e9d5ff'];
  public vistoriasChart: ApexChart = { type: 'donut', height: 320 };
  public vistoriasPlotOptions: any = {
    pie: {
      donut: {
        size: '68%'
      },
      expandOnClick: true
    }
  };
  public vistoriasLegend: any = {
    position: 'right',
    horizontalAlign: 'center',
    formatter: function(seriesName: string, opts: any) {
      return seriesName;
    }
  };
  public vistoriasDataLabels: any = {
    enabled: true,
    formatter: (val: number, opts?: any) => {
      // show percentage with one decimal
      return val !== undefined ? val.toFixed(1) + '%' : '';
    }
  };

  // Target/progress
  public objetivo = 10000; // default target
  // stepSize removed — single input used
  public totalCadastros = 0;
  public totalRecadastros = 0;
  public totalGeral = 0;
  public progressoPercent = 0;

  // progress chart config (Apex radial)
  public progressoSeries: ApexNonAxisChartSeries = [0];
  public progressoChart: ApexChart = { type: 'radialBar', height: 200, animations: { enabled: true, easing: 'easeinout', speed: 800 } } as any;
  public progressoPlotOptions: any = {
    radialBar: {
      startAngle: -120,
      endAngle: 120,
      hollow: {
        size: '70%'
      },
      track: {
        background: '#f3f4f6',
        strokeWidth: '80%'
      },
      dataLabels: {
        show: true,
        name: {
          show: false
        },
        value: {
          show: true,
          fontSize: '18px',
          color: '#111827',
          formatter: function (val: number) {
            return val !== undefined ? val.toFixed(1) + '%' : '0%';
          }
        }
      }
    }
  };
  public progressoColors: string[] = ['#0ea5e9'];
  public faltam = 0;

  loading = false;
  errorMessage = '';

  constructor(private servico: ServicoService) {}

  ngOnInit(): void {
    this.updateDateFilter();
    // load objetivo from localStorage if present
    const stored = localStorage.getItem('dashboard_objetivo');
    if (stored) {
      const v = Number(stored);
      if (!isNaN(v) && v > 0) this.objetivo = Math.round(v);
    }
    this.loadAndBuildCharts();
  }

  onPeriodoChange() {
    this.updateDateFilter();
    this.loadAndBuildCharts();
  }

  onDataInicialChange(value: string) {
    this.dataInicialInput = value;
    this.onPeriodoChange();
  }

  onDataFinalChange(value: string) {
    this.dataFinalInput = value;
    this.onPeriodoChange();
  }

  private updateDateFilter(): void {
    const now = new Date();
    const yyyy = (d: Date) => d.getFullYear();
    const mm = (d: Date) => String(d.getMonth() + 1).padStart(2, '0');
    const dd = (d: Date) => String(d.getDate()).padStart(2, '0');

    const format = (d: Date, end = false) => `${yyyy(d)}-${mm(d)}-${dd(d)}T${end ? '23:59:59' : '00:00:00'}`;

    if (this.periodoSelecionado === 'hoje') {
      this.dataInicialInput = format(now).split('T')[0];
      this.dataFinalInput = format(now, true).split('T')[0];
    } else if (this.periodoSelecionado === 'semana') {
      const lastWeek = new Date(now);
      lastWeek.setDate(now.getDate() - 7);
      this.dataInicialInput = format(lastWeek).split('T')[0];
      this.dataFinalInput = format(now, true).split('T')[0];
    } else if (this.periodoSelecionado === 'mes') {
      const lastMonth = new Date(now);
      lastMonth.setMonth(now.getMonth() - 1);
      this.dataInicialInput = format(lastMonth).split('T')[0];
      this.dataFinalInput = format(now, true).split('T')[0];
    } else if (this.periodoSelecionado === 'custom') {
      // keep inputs as-is
    }
  }

  private buildParams(): { headers: HttpHeaders; params: HttpParams } {
    const token = localStorage.getItem('token');
    const headers = new HttpHeaders({ Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' });
    const dataInicial = this.dataInicialInput ? `${this.dataInicialInput}T00:00:00` : '';
    const dataFinal = this.dataFinalInput ? `${this.dataFinalInput}T23:59:59` : '';
    let params = new HttpParams();
    if (dataInicial) params = params.set('dataInicial', dataInicial);
    if (dataFinal) params = params.set('dataFinal', dataFinal);
    return { headers, params };
  }

  loadAndBuildCharts(): void {
    this.loading = true;
    this.errorMessage = '';

    // Use the existing service endpoint `obterTodos` (returns { itens: [], ... })
    const dataInicio = this.dataInicialInput ? `${this.dataInicialInput}T00:00:00` : undefined;
    const dataFim = this.dataFinalInput ? `${this.dataFinalInput}T23:59:59` : undefined;

    // pass municipio (cidade) as the 13th argument if selected
    const cidadeArg = this.municipioSelecionado && this.municipioSelecionado !== '' ? this.municipioSelecionado : undefined;

    this.servico.obterTodos(dataInicio, dataFim, 1, 1000, undefined, undefined, undefined, undefined, undefined, undefined, undefined, undefined, cidadeArg).subscribe({
      next: (resp) => {
        const itens: VistoriaItem[] = resp && resp.itens ? resp.itens : [];
        this.processVistorias(itens);
        this.loading = false;
      },
      error: (err: HttpErrorResponse) => {
        console.error('Erro ao carregar vistorias', err);
        this.errorMessage = 'Erro ao carregar dados';
        this.loading = false;
      }
    });
  }

  private getMunicipioFromItem(item: VistoriaItem): string {
    if (!item) return 'Desconhecido';
    // Prefer endereco.cidade, then nomeConsumidor/matricula as fallback
    const cidade = item.endereco?.cidade;
    if (cidade) return cidade;
    // Some records may include municipio in other places
    const possible = (item as any).municipio || item.nomeConsumidor || item.matricula || item.numeroOS;
    return possible || 'Desconhecido';
  }

  private processVistorias(itens: VistoriaItem[]): void {
    // Build maps
    const cadMap: Record<string, number> = {};
    const recadMap: Record<string, number> = {};
  const statusCounts = { concluido: 0, pendente: 0, negado: 0, outros: 0 } as { concluido: number; pendente: number; negado: number; outros: number };
    const municipiosSet = new Set<string>();

    itens.forEach(item => {
      const municipio = this.getMunicipioFromItem(item) || 'Desconhecido';
      municipiosSet.add(municipio);

      // Determine recadastro by recadastroId presence or tipoVistoria
      const isRecadastro = !!item.recadastroId && item.recadastroId !== '00000000-0000-0000-0000-000000000000'
        || (item.tipoVistoria || '').toLowerCase().includes('recad');

      if (isRecadastro) {
        recadMap[municipio] = (recadMap[municipio] || 0) + 1;
      } else {
        cadMap[municipio] = (cadMap[municipio] || 0) + 1;
      }

      const status = (item.status || '').toLowerCase();
      if (status.includes('concl')) statusCounts.concluido += 1;
      else if (status.includes('pend')) statusCounts.pendente += 1;
      else if (status.includes('neg')) statusCounts.negado += 1;
      else statusCounts.outros += 1;
    });


    // Sort municipios by total count desc and keep labels consistent between cad/recad
    const allMunicipios = Array.from(municipiosSet);
    const sortedAll = allMunicipios.sort((a, b) => {
      const ta = (cadMap[a] || 0) + (recadMap[a] || 0);
      const tb = (cadMap[b] || 0) + (recadMap[b] || 0);
      return tb - ta;
    });
    // top 20 for charts/table
    const sorted = sortedAll.slice(0, 20);

    this.distinctMunicipios = municipiosSet.size;
    // provide options for filter (include all sorted municipalities)
    this.municipiosOptions = sortedAll;

    // Prepare chart datasets
    // Instead of per-municipio, build totals per day (cadastros + recadastros) according to the date filter
    const dateCounts: Record<string, number> = {};
    const parseDateKey = (iso?: string | null) => {
      if (!iso) return null;
      const d = new Date(iso);
      if (isNaN(d.getTime())) return null;
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const dd = String(d.getDate()).padStart(2, '0');
      return `${y}-${m}-${dd}`;
    };

    // Count each item on the date it was created (or recadastramento date for recadastros when available)
    // We'll build separate counts for cadastros and recadastros per day
    const dateCountsCad: Record<string, number> = {};
    const dateCountsRecad: Record<string, number> = {};
    itens.forEach(item => {
      const isRecadastro = !!item.recadastroId && item.recadastroId !== '00000000-0000-0000-0000-000000000000'
        || (item.tipoVistoria || '').toLowerCase().includes('recad');
      const dateIso = isRecadastro ? (item.dataRecadastramento || item.dataCadastroOriginal) : (item.dataCadastroOriginal || item.dataRecadastramento);
      const key = parseDateKey(dateIso);
      if (!key) return;
      if (isRecadastro) dateCountsRecad[key] = (dateCountsRecad[key] || 0) + 1;
      else dateCountsCad[key] = (dateCountsCad[key] || 0) + 1;
      // also keep overall for compatibility
      dateCounts[key] = (dateCounts[key] || 0) + 1;
    });

    // Determine start/end for the x-axis from filters (or fall back to data range)
    let startDate: Date | null = this.dataInicialInput ? new Date(this.dataInicialInput) : null;
    let endDate: Date | null = this.dataFinalInput ? new Date(this.dataFinalInput) : null;
    if (!startDate || isNaN(startDate.getTime()) || !endDate || isNaN(endDate.getTime())) {
      const keys = Object.keys(dateCounts).sort();
      if (keys.length) {
        startDate = new Date(keys[0]);
        endDate = new Date(keys[keys.length - 1]);
      } else {
        startDate = new Date();
        endDate = new Date();
      }
    }

    // Build labels (dd/MM) and data array iterating day-by-day
  const dateLabels: string[] = [];
  const seriesData: number[] = [];
  const seriesCad: number[] = [];
  const seriesRecad: number[] = [];
    const cur = new Date(startDate);
    // normalize time
    cur.setHours(0, 0, 0, 0);
    endDate.setHours(0, 0, 0, 0);
    while (cur <= endDate) {
      const y = cur.getFullYear();
      const m = String(cur.getMonth() + 1).padStart(2, '0');
      const d = String(cur.getDate()).padStart(2, '0');
      const key = `${y}-${m}-${d}`;
      dateLabels.push(`${d}/${m}`);
      const cadVal = dateCountsCad[key] || 0;
      const recadVal = dateCountsRecad[key] || 0;
      seriesCad.push(cadVal);
      seriesRecad.push(recadVal);
      seriesData.push((dateCounts[key] || 0));
      cur.setDate(cur.getDate() + 1);
    }

  // expose daily arrays for template (totals and per-type series)
  this.dailyLabels = dateLabels;
  this.dailyTotals = seriesData;
  // also provide cad and recad arrays
  this.dailyCad = seriesCad;
  this.dailyRecad = seriesRecad;
    this.dailyDeltas = [];
    this.dailyDeltaPercents = [];
    for (let i = 0; i < seriesData.length; i++) {
      if (i === 0) {
        this.dailyDeltas.push(0);
        this.dailyDeltaPercents.push(0);
      } else {
        const prev = seriesData[i - 1] || 0;
        const curVal = seriesData[i] || 0;
        const delta = curVal - prev;
        this.dailyDeltas.push(delta);
        // percent change relative to previous day; if prev === 0, use 100% when cur>0, else 0
        const pct = prev === 0 ? (curVal === 0 ? 0 : 100) : +(delta / prev * 100);
        this.dailyDeltaPercents.push(+pct.toFixed(1));
      }
    }

    this.combinedXAxis.categories = dateLabels;
    // Combine cadastros + recadastros as side-by-side columns
    this.combinedSeries = [
      { name: 'Cadastros', type: 'column', data: seriesCad },
      { name: 'Recadastros', type: 'column', data: seriesRecad }
    ] as any;

    this.statusSeries = [statusCounts['concluido'], statusCounts['pendente'], statusCounts['negado'], statusCounts['outros']];

    // Prepare municipality rows with percentages
  const totalCad = Object.values(cadMap).reduce((s, v) => s + v, 0);
  const totalRecad = Object.values(recadMap).reduce((s, v) => s + v, 0);

    this.municipioRows = sorted.map(name => {
      const cad = cadMap[name] || 0;
      const recad = recadMap[name] || 0;
      return {
        nome: name,
        cadastros: cad,
        recadastros: recad,
        cadPercent: totalCad > 0 ? +(cad / totalCad * 100).toFixed(1) : 0,
        recadPercent: totalRecad > 0 ? +(recad / totalRecad * 100).toFixed(1) : 0,
      };
    });

    // Update totals and progress
    this.totalCadastros = totalCad;
    this.totalRecadastros = totalRecad;
    this.totalGeral = totalCad + totalRecad;
    this.progressoPercent = this.objetivo > 0 ? +(this.totalGeral / this.objetivo * 100).toFixed(1) : 0;
    this.progressoSeries = [Math.min(this.progressoPercent, 999)];
    this.faltam = Math.max(0, this.objetivo - this.totalGeral);
    this.updateProgressoColor();

  // Build combined pie data (top 10) — vistorias = cad + recad
  const pieTop = sorted.slice(0, 10);
  this.vistoriasPieLabels = pieTop;
  this.vistoriasPieSeries = pieTop.map(s => (cadMap[s] || 0) + (recadMap[s] || 0));
  // generate distinct colors for each municipality
  this.vistoriasPieColors = this.generateColors(this.vistoriasPieLabels.length);
  }

  private generateColors(n: number): string[] {
    const colors: string[] = [];
    for (let i = 0; i < n; i++) {
      const hue = Math.round((i * 360) / n);
      colors.push(this.hslToHex(hue, 70, 50));
    }
    return colors;
  }

  private hslToHex(h: number, s: number, l: number): string {
    // convert HSL to RGB then to hex
    s /= 100;
    l /= 100;
    const k = (n: number) => (n + h / 30) % 12;
    const a = s * Math.min(l, 1 - l);
    const f = (n: number) => {
      const color = l - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));
      return Math.round(255 * color);
    };
    const toHex = (x: number) => x.toString(16).padStart(2, '0');
    return `#${toHex(f(0))}${toHex(f(8))}${toHex(f(4))}`;
  }

  onObjetivoChange(value: string) {
    const n = Number(value);
    this.objetivo = isNaN(n) || n <= 0 ? 0 : Math.round(n);
    this.progressoPercent = this.objetivo > 0 ? +(this.totalGeral / this.objetivo * 100).toFixed(1) : 0;
    this.progressoSeries = [Math.min(this.progressoPercent, 999)];
    this.faltam = Math.max(0, this.objetivo - this.totalGeral);
    this.updateProgressoColor();
    // persist
    localStorage.setItem('dashboard_objetivo', String(this.objetivo));
  }

  private updateProgressoColor() {
    // choose color based on progress
    if (this.progressoPercent >= 100) this.progressoColors = ['#16a34a']; // green
    else if (this.progressoPercent >= 50) this.progressoColors = ['#f59e0b']; // amber
    else this.progressoColors = ['#0ea5e9']; // blue
  }

}
